import { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/cn';
import { api } from '@/api/client';
import {
    type PaymentIntentResponse,
    useUpdatePaymentIntentSaveFutureUsage,
} from '@/api/portalPayments';

// Phase 4 of docs/build-plans/pma-stripe-payments.md.
//
// Embedded Stripe Elements form for parent-portal invoice payments.
// `loadStripe()` is cached per publishable key — the SPA reads the key
// from the PaymentIntent response (NOT a build-time env var) so a
// future platform-key rotation doesn't require a rebuild.
//
// Two states managed at this level:
//   1. fetching intent  — call useCreatePaymentIntent on mount, show spinner
//   2. ready            — wrap PaymentElement in <Elements> and render
//
// stripe.confirmPayment runs client-side and may return success, but
// the Phase 3 webhook is the source of truth for invoice state. The
// success panel tells the parent the payment was submitted; the
// surrounding invoice query gets invalidated so the next refetch
// picks up `paidInPortalAt` once the webhook fires.

// Stripe Promise cache. loadStripe() makes a network request; we don't
// want to fire it on every form mount. Keyed by (publishable key,
// stripeAccount) since Stripe.js routes elements/sessions calls through
// the connected account at SDK-init time — a fresh Stripe instance is
// needed if the account ever changes (today every practice has at most
// one, but the key includes both to future-proof).
const stripePromises = new Map<string, Promise<Stripe | null>>();

function getStripePromise(publishableKey: string, stripeAccount: string): Promise<Stripe | null> {
    const cacheKey = `${publishableKey}::${stripeAccount}`;
    let p = stripePromises.get(cacheKey);
    if (!p) {
        // stripeAccount tells Stripe.js to query the connected account's
        // PaymentIntent — Phase 2 mints the intent on the practice's
        // Stripe Connect account, not the platform account. Without this
        // option, /v1/elements/sessions 400s with a key/intent mismatch
        // and the form stays blank.
        p = loadStripe(publishableKey, { stripeAccount });
        stripePromises.set(cacheKey, p);
    }
    return p;
}

interface Props {
    invoiceId: string;
    invoiceDisplayId: string;
    amountCents: number;
    /** Called after stripe.confirmPayment resolves with no error.
     *
     *  `result.isDelayedSettlement` is true when the underlying
     *  Stripe PaymentIntent landed in `processing` (ACH and other
     *  delayed-settlement methods) rather than `succeeded`. The parent
     *  uses it to swap the success banner copy: card → "we'll update
     *  in a few seconds", ACH → "your bank transfer will settle in 3–5
     *  business days, the invoice will update automatically when it
     *  does."
     *  The parent should invalidate the invoice query so the next fetch
     *  picks up paidInPortalAt once the Phase 3 webhook flips it. */
    onSucceeded?: (result: { isDelayedSettlement: boolean }) => void;
    onCancel?: () => void;
    /** Phase 8 — when true, render the "Save this card for future
     *  payments & autopay" checkbox inside the form. Toggling it
     *  flips setup_future_usage on the in-flight PaymentIntent via
     *  the save-future-usage route, so the form does NOT remount. */
    allowSaveCard?: boolean;
}

interface RawIntentResponse {
    client_secret: string;
    publishable_key: string;
    stripe_account: string;
    business_name?: string | null;
    reused?: boolean;
}

export function InvoicePaymentForm({
    invoiceId, invoiceDisplayId, amountCents, onSucceeded, onCancel, allowSaveCard = false,
}: Props) {
    const [data, setData] = useState<PaymentIntentResponse | null>(null);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const fired = useRef(false);

    // Fire the create-intent POST exactly once per form mount via local
    // useState — NOT React Query's useMutation. Why: in React 19
    // StrictMode (dev), the mount → cleanup → remount cycle tears down
    // the mutation observer between the two effect runs. The first
    // mutate's response then resolves to a stale observer the component
    // no longer subscribes to, and the new observer stays in `idle`
    // (isPending=false, isSuccess=false) — the spinner condition
    // `isPending || (!isSuccess && !error)` evaluates `false ||
    // (true && true)` and the form hangs forever even though the
    // network call returned 200 with a valid client_secret.
    //
    // useState + a useRef guard sidesteps the observer lifecycle
    // entirely: state writes are guaranteed to re-render the (same)
    // component instance, and the guard keeps StrictMode's double-fire
    // from re-POSTing.
    useEffect(() => {
        if (fired.current) return;
        fired.current = true;
        if (!invoiceId) {
            setError(new Error('Cannot start payment — no invoice id.'));
            return;
        }
        api.post<RawIntentResponse>(
            `/portal/invoices/${invoiceId}/payment-intent`,
            // Pass the parent-chosen amount through — server clamps it
            // against the remaining balance and the $10 floor. Omitting
            // amount_cents falls back to the full balance for callers
            // (e.g. tests) that don't pass a value. Phase 8: save_card
            // is NEVER passed at create time anymore — the in-form
            // checkbox flips setup_future_usage via the save-future-
            // usage route so the form doesn't remount on toggle.
            { amount_cents: amountCents },
        )
            .then((res) => {
                const { client_secret, publishable_key, stripe_account, reused } = res.data;
                if (!client_secret || !publishable_key || !stripe_account) {
                    setError(new Error('Server did not return a payable PaymentIntent.'));
                    return;
                }
                setData({
                    clientSecret:   client_secret,
                    publishableKey: publishable_key,
                    stripeAccount:  stripe_account,
                    businessName:   res.data.business_name ?? null,
                    reused,
                });
                // The clientSecret encodes the PaymentIntent id as its
                // first underscore-separated segment (`pi_xxx_secret_yyy`).
                // Pull it out so the save-future-usage route has the
                // intent id to update without a separate round-trip.
                const cs = client_secret.split('_secret_')[0];
                setPaymentIntentId(cs || null);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err : new Error('Unable to start payment.'));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
        return (
            <div className="space-y-3">
                <Alert variant="danger">{error.message}</Alert>
                {onCancel && (
                    <Button variant="ghost" size="sm" onClick={onCancel}>
                        Back to invoice
                    </Button>
                )}
            </div>
        );
    }

    if (!data) {
        return (
            <div className="py-6 flex items-center justify-center text-text-subtle text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Preparing secure payment form…
            </div>
        );
    }

    return (
        <ElementsHost
            invoiceId={invoiceId}
            paymentIntentId={paymentIntentId}
            clientSecret={data.clientSecret}
            publishableKey={data.publishableKey}
            stripeAccount={data.stripeAccount}
            businessName={data.businessName}
            invoiceDisplayId={invoiceDisplayId}
            amountCents={amountCents}
            onSucceeded={onSucceeded}
            onCancel={onCancel}
            allowSaveCard={allowSaveCard}
        />
    );
}

interface ElementsHostProps {
    invoiceId: string;
    paymentIntentId: string | null;
    clientSecret: string;
    publishableKey: string;
    stripeAccount: string;
    businessName: string | null;
    invoiceDisplayId: string;
    amountCents: number;
    onSucceeded?: (result: { isDelayedSettlement: boolean }) => void;
    onCancel?: () => void;
    allowSaveCard: boolean;
}

function ElementsHost({
    invoiceId, paymentIntentId, clientSecret, publishableKey, stripeAccount, businessName,
    invoiceDisplayId, amountCents, onSucceeded, onCancel, allowSaveCard,
}: ElementsHostProps) {
    const stripePromise = useMemo(
        () => getStripePromise(publishableKey, stripeAccount),
        [publishableKey, stripeAccount],
    );
    return (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <PaymentInner
                invoiceId={invoiceId}
                paymentIntentId={paymentIntentId}
                businessName={businessName}
                invoiceDisplayId={invoiceDisplayId}
                amountCents={amountCents}
                onSucceeded={onSucceeded}
                onCancel={onCancel}
                allowSaveCard={allowSaveCard}
            />
        </Elements>
    );
}

interface PaymentInnerProps {
    invoiceId: string;
    paymentIntentId: string | null;
    businessName: string | null;
    invoiceDisplayId: string;
    amountCents: number;
    onSucceeded?: (result: { isDelayedSettlement: boolean }) => void;
    onCancel?: () => void;
    allowSaveCard: boolean;
}

function PaymentInner({
    invoiceId, paymentIntentId, businessName, invoiceDisplayId, amountCents,
    onSucceeded, onCancel, allowSaveCard,
}: PaymentInnerProps) {
    const stripe   = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Phase 8 follow-up — TWO local checkboxes. saveCard alone uses
    // on_session (save without autopay mandate). saveCard + autopay
    // uses off_session (save + autopay mandate). The mutation flips
    // the in-flight intent's setup_future_usage on every toggle so
    // the form doesn't remount.
    const [saveCard, setSaveCard] = useState(false);
    const [autopayConsent, setAutopayConsent] = useState(false);
    const [saveBusy, setSaveBusy] = useState(false);
    const saveError = useRef<string | null>(null);
    const updateSave = useUpdatePaymentIntentSaveFutureUsage(invoiceId, paymentIntentId);

    async function applyMode(nextSave: boolean, nextAutopay: boolean) {
        if (!paymentIntentId) return;
        // Optimistic local update so the checkboxes feel responsive;
        // revert on failure.
        const priorSave = saveCard;
        const priorAutopay = autopayConsent;
        setSaveCard(nextSave);
        setAutopayConsent(nextAutopay);
        saveError.current = null;
        setSaveBusy(true);
        const mode = !nextSave
            ? 'off'
            : (nextAutopay ? 'save_and_autopay' : 'save_only');
        try {
            await updateSave.mutateAsync(mode);
        } catch (e) {
            setSaveCard(priorSave);
            setAutopayConsent(priorAutopay);
            saveError.current = e instanceof Error ? e.message : 'Could not update.';
        } finally {
            setSaveBusy(false);
        }
    }

    function toggleSaveCard(next: boolean) {
        // Unchecking save also clears autopay (autopay requires save).
        applyMode(next, next ? autopayConsent : false);
    }
    function toggleAutopay(next: boolean) {
        // Autopay can only be toggled while save is checked.
        if (!saveCard) return;
        applyMode(true, next);
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setErrorMessage(null);
        // redirect:'if_required' keeps the parent on the portal page; 3DS
        // cards still get their challenge via a Stripe-hosted modal.
        const result = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
        });
        if (result.error) {
            setSubmitting(false);
            // Card_error and validation_error are safe to surface; other
            // types (api_error, invalid_request_error) are operator-level
            // — show a generic message.
            const type = result.error.type;
            const safe = type === 'card_error' || type === 'validation_error';
            setErrorMessage(safe && result.error.message
                ? result.error.message
                : 'Payment could not be processed. Please try again or use a different card.');
            return;
        }

        // Race ahead of the webhook: call PMA's confirm route now so
        // the invoice's paid_amount + status update synchronously, in
        // the same request, before we re-fetch. Without this the SPA
        // has to poll until Stripe delivers payment_intent.succeeded
        // (1–5 seconds) and the StatusBadge stays stale meanwhile.
        // Server-side this is idempotent with the webhook (both paths
        // share applyPaymentIntentSucceeded with a status != 'succeeded'
        // guard).
        const paymentIntentId = result.paymentIntent?.id;
        if (paymentIntentId) {
            try {
                await api.post(
                    `/portal/invoices/${invoiceId}/payment-intent/${paymentIntentId}/confirm`,
                );
            } catch (confirmErr) {
                // Not fatal — Stripe charged the card and the webhook will
                // eventually catch up. Log silently; the UI still shows
                // the success panel so the parent doesn't think the
                // payment failed.
                // eslint-disable-next-line no-console
                console.warn('[InvoicePaymentForm] confirm-route failed; webhook will reconcile', confirmErr);
            }
        }

        setSubmitting(false);
        // ACH + other delayed-settlement methods land in 'processing'
        // straight out of confirmPayment — the bank settles 3-5
        // business days later. Cards go to 'succeeded' immediately.
        // The parent banner uses this hint to swap copy without
        // having to inspect the PaymentIntent itself.
        const isDelayedSettlement = result.paymentIntent?.status === 'processing';
        onSucceeded?.({ isDelayedSettlement });
        // No local success panel — the parent owns the success surface
        // now (the form is unmounted by the parent collapsing Step 1/2
        // on paySubmittedAt being set). Showing a local panel here
        // would flash a brief duplicate green box inside Step 2 before
        // the collapse + the parent's banner appears.
    }

    const amountLabel = `$${(amountCents / 100).toFixed(2)}`;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-sm text-text-muted">
                Paying <span className="font-medium text-text">{amountLabel}</span> for{' '}
                <span className="font-medium text-text">{invoiceDisplayId}</span>.
            </div>
            <PaymentElement
                options={{
                    // business.name overrides Stripe's mandate-text
                    // merchant display ("you allow X to charge your
                    // card…") so the parent sees the practice name
                    // instead of the connected account's profile name
                    // (which in test mode reads the placeholder Stripe
                    // onboarding accepts).
                    ...(businessName ? { business: { name: businessName } } : {}),
                    // Defensive Link suppression. Setting wallets.link
                    // is the documented PaymentElement option in newer
                    // Stripe.js releases; if the SDK we're on doesn't
                    // honor it, this is a no-op (Stripe.js ignores
                    // unknown options). The authoritative Link disable
                    // still happens in the Stripe Dashboard's Link
                    // sign-up setting.
                    wallets: { link: 'never', applePay: 'auto', googlePay: 'auto' },
                } as React.ComponentProps<typeof PaymentElement>['options']}
            />
            {/*
              Two-checkbox model. Save without autopay uses on_session
              (no mandate text from Stripe); save + autopay uses
              off_session (mandate text appears below — Stripe-rendered
              and required by network rules for merchant-initiated
              transactions). Toggling either fires the save-future-
              usage route to flip the in-flight intent; no remount.
            */}
            {allowSaveCard && paymentIntentId && (
                <div className="space-y-2 text-sm text-text-muted">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={saveCard}
                            onChange={(e) => toggleSaveCard(e.target.checked)}
                            disabled={saveBusy || submitting}
                            className="mt-0.5"
                        />
                        <span>Save this card for future one-click payments</span>
                    </label>
                    <label className={cn(
                        'flex items-start gap-2 ml-6',
                        saveCard ? 'cursor-pointer' : 'cursor-not-allowed text-text-subtle',
                    )}>
                        <input
                            type="checkbox"
                            checked={autopayConsent}
                            onChange={(e) => toggleAutopay(e.target.checked)}
                            disabled={!saveCard || saveBusy || submitting}
                            className="mt-0.5"
                        />
                        <span>Allow this card to be used for autopay on future invoices</span>
                    </label>
                    {saveError.current && (
                        <p className="text-xs text-error-text">{saveError.current}</p>
                    )}
                </div>
            )}
            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
            <div className="flex items-center gap-2 pt-1">
                <Button type="submit" variant="success" disabled={!stripe || !elements || submitting || saveBusy}>
                    {submitting ? 'Processing…' : `Pay ${amountLabel}`}
                </Button>
                {onCancel && (
                    <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                        Cancel
                    </Button>
                )}
            </div>
        </form>
    );
}
